import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import ConfirmationModal from '../../../view/components/ConfirmationModal';
import BirthForm from '../../../view/components/Birth/BirthForm';
import DeleteServiceConflictModal from '../../../view/components/Catalog/DeleteServiceConflictModal';
import CreateGalleryModal from '../../../view/components/Gallery/CreateGalleryModal';
import GalleryManagerModal from '../../../view/components/Gallery/GalleryManagerModal';
import DeactivateLeadModal from '../../../view/components/Lead/DeactivateLeadModal';
import MessageComposer from '../../../view/components/Lead/Detail/MessageComposer';
import RegisterSaleModal from '../../../view/components/Lead/Detail/RegisterSaleModal';
import Modal from '../../../view/components/Modal';
import BadgeSettingsPanel from '../../../view/components/Settings/BadgeSettingsPanel';
import EventChecklistModal from '../../../view/components/Settings/EventChecklistModal';
import MessageTemplateModal from '../../../view/components/Settings/MessageTemplateModal';
import IssueContractModal from '../../../view/components/Contract/IssueContractModal';
import SendForSignatureModal from '../../../view/components/Contract/SendForSignatureModal';
import TemplateModal from '../../../view/components/Contract/TemplateModal';
import ContractorModal from '../../../view/components/Settings/ContractorModal';
import EventForm from '../../../view/components/Register/EventForm';
import LeadForm from '../../../view/components/Register/LeadForm';
import RegisterServiceForm from '../../../view/components/Register/RegisterServiceForm';
import RegisterServicePackageForm from '../../../view/components/Register/RegisterServicePackageForm';
import { TodoModal } from '../../../view/components/Register/TodoModal';
import ChangePasswordForm from '../../../view/components/Settings/ChangePasswordForm';
import { ModalTypeEnum } from '../../enums/ModalTypeEnum';
import { useStore } from '../../store';

export function ModalManager() {
  const {
    openModal,
    lead,
    event,
    leadId,
    eventStatusId,
    isConfirmationModalOpen,
    confirmationModalProps,
    handleCloseModal,
    service,
    servicePackage,
    deleteServiceConflict,
    registerSaleLeadId,
    contractor,
    contractTemplate,
    issueContractLeadId,
    sendForSignature,
    messageTemplate,
    messageComposerLead,
    eventChecklist,
    deactivateLead,
    eventDraft,
    createGalleryLeadId,
    manageGalleryId,
    birth,
    birthLeadId,
  } = useStore(
    useShallow((state) => ({
      openModal: state.modal.openModal,
      lead: state.modal.lead,
      leadId: state.modal.leadId,
      event: state.modal.event,
      eventStatusId: state.modal.eventStatusId,
      isConfirmationModalOpen: state.modal.isConfirmationModalOpen,
      confirmationModalProps: state.modal.confirmationModalProps,
      handleCloseModal: state.modal.handleCloseModal,
      service: state.modal.service,
      servicePackage: state.modal.servicePackage,
      deleteServiceConflict: state.modal.deleteServiceConflict,
      registerSaleLeadId: state.modal.registerSaleLeadId,
      contractor: state.modal.contractor,
      contractTemplate: state.modal.contractTemplate,
      issueContractLeadId: state.modal.issueContractLeadId,
      sendForSignature: state.modal.sendForSignature,
      messageTemplate: state.modal.messageTemplate,
      messageComposerLead: state.modal.messageComposerLead,
      eventChecklist: state.modal.eventChecklist,
      deactivateLead: state.modal.deactivateLead,
      eventDraft: state.modal.eventDraft,
      createGalleryLeadId: state.modal.createGalleryLeadId,
      manageGalleryId: state.modal.manageGalleryId,
      birth: state.modal.birth,
      birthLeadId: state.modal.birthLeadId,
    })),
  );
  const { t } = useTranslation();

  return (
    <>
      {openModal === ModalTypeEnum.Lead && (
        <Modal
          className="w-[500px]"
          title={t(`register.title.${lead ? 'editing' : 'new'}.lead`)}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <LeadForm lead={lead} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.Event && (
        <Modal
          className="w-[500px]"
          title={t(`register.title.${event ? 'editing' : 'new'}.event`)}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <EventForm
            event={event}
            leadId={leadId || event?.leadId}
            eventStatusId={eventStatusId}
            eventDraft={eventDraft}
          />
        </Modal>
      )}
      <TodoModal />
      {openModal === ModalTypeEnum.Service && (
        <Modal
          className="w-[500px]"
          title={t(
            service
              ? 'catalog.form.title.editService'
              : 'catalog.form.title.createService',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <RegisterServiceForm service={service} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.ServicePackage && (
        <Modal
          className="w-[600px]"
          title={t(
            servicePackage
              ? 'catalog.form.title.editPackage'
              : 'catalog.form.title.createPackage',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <RegisterServicePackageForm servicePackage={servicePackage} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.DeleteServiceConflict &&
        deleteServiceConflict && (
          <Modal
            className="w-[480px]"
            title={t('catalog.conflictModal.title')}
            titlePosition="center"
            onClose={handleCloseModal}
          >
            <DeleteServiceConflictModal
              service={deleteServiceConflict.service}
              packages={deleteServiceConflict.packages}
            />
          </Modal>
        )}
      {openModal === ModalTypeEnum.RegisterSale && registerSaleLeadId && (
        <Modal
          className="w-[640px]"
          title={t('sale.registerModal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <RegisterSaleModal leadId={registerSaleLeadId} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.ChangePassword && (
        <Modal
          className="w-[500px]"
          title={t('settings.changePassword.modal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <ChangePasswordForm />
        </Modal>
      )}
      {openModal === ModalTypeEnum.Contractor && (
        <Modal
          className="w-[560px]"
          title={t(
            contractor
              ? 'contract.contractor.modal.titleEdit'
              : 'contract.contractor.modal.titleCreate',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <ContractorModal />
        </Modal>
      )}
      {openModal === ModalTypeEnum.ContractTemplate && (
        <Modal
          className="w-full md:w-[768px] lg:w-[960px] xl:w-[1100px]"
          title={t(
            contractTemplate
              ? 'contract.templates.modal.titleEdit'
              : 'contract.templates.modal.titleCreate',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <TemplateModal />
        </Modal>
      )}
      {openModal === ModalTypeEnum.IssueContract && issueContractLeadId && (
        <Modal
          className="w-[640px]"
          title={t('contract.issue.modal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <IssueContractModal />
        </Modal>
      )}
      {openModal === ModalTypeEnum.SendForSignature && sendForSignature && (
        <Modal
          className="w-full md:w-[520px]"
          title={t('contract.signature.modal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <SendForSignatureModal />
        </Modal>
      )}
      {openModal === ModalTypeEnum.ManageBadges && (
        <Modal
          className="w-[640px]"
          title={t('badge.settings.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <BadgeSettingsPanel />
        </Modal>
      )}
      {openModal === ModalTypeEnum.MessageTemplate && (
        <Modal
          className="w-[560px]"
          title={t(
            messageTemplate
              ? 'settings.messageTemplates.modal.titleEdit'
              : 'settings.messageTemplates.modal.titleCreate',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <MessageTemplateModal messageTemplate={messageTemplate} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.MessageComposer && messageComposerLead && (
        <Modal
          className="w-[560px]"
          title={t('lead.messageComposer.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <MessageComposer lead={messageComposerLead} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.EventChecklist && (
        <Modal
          className="w-[600px]"
          title={t(
            eventChecklist
              ? 'settings.checklists.modal.titleEdit'
              : 'settings.checklists.modal.titleCreate',
          )}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <EventChecklistModal eventChecklist={eventChecklist} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.DeactivateLead && deactivateLead && (
        <Modal
          className="w-[500px]"
          title={t('lead.deactivate.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <DeactivateLeadModal lead={deactivateLead} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.CreateGallery && createGalleryLeadId && (
        <Modal
          className="w-[500px]"
          title={t('gallery.createModal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <CreateGalleryModal leadId={createGalleryLeadId} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.ManageGallery && manageGalleryId && (
        <Modal
          className="flex max-h-[85vh] w-full flex-col overflow-hidden md:w-[768px] lg:w-[960px]"
          title={t('gallery.manageModal.title')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <GalleryManagerModal galleryId={manageGalleryId} />
        </Modal>
      )}
      {openModal === ModalTypeEnum.Birth && birthLeadId && (
        <Modal
          className="w-full md:w-[500px]"
          title={t(birth ? 'birth.form.titleEdit' : 'birth.form.titleCreate')}
          titlePosition="center"
          onClose={handleCloseModal}
        >
          <BirthForm leadId={birthLeadId} birth={birth} />
        </Modal>
      )}
      {isConfirmationModalOpen && confirmationModalProps && (
        <ConfirmationModal {...confirmationModalProps} />
      )}
    </>
  );
}
